import { PsHttpService, type PatSnapSuccessResponseData } from '@patsnap/tahiti-core';
import type { CreateNoveltyCheckTaskApi, CreateNoveltyCheckTaskRequest, CreateNoveltyCheckTaskResponse } from '../create-novelty-check-task-api';
import { coreTelecomApiUrlPrefix } from '@telecom/shared/domain-config';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { FeatureRecognizeApi, FeatureRecognizeRequest, FeatureRecognizeResponse } from '../feature-recognize-api';
import type { NoveltyInputSummaryApi, SummarizeNoveltyInputRequest, SummarizeNoveltyInputResponse } from '../novelty-input-summary-api';
import type {
    PatentProfileByIdsApi,
    PatentProfileByIdsRequest,
    PatentProfileByIdsResponse,
    PatentProfileByKeywordRequest,
    PatentProfileByKeywordResponse,
} from '../patent-profile-by-ids-api';
import type { SearchNoveltyApi, SearchNoveltyRequest, SearchNoveltyResponse } from '../search-novelty-api';
import type { SearchNoveltyResultApi, SearchNoveltyResultRequest, SearchNoveltyResultResponseV2 } from '../search-novelty-result-api';
import type { TechnicalFeaturesGetApi, TechnicalFeaturesGetApiRequest, TechnicalFeaturesGetApiResponse, TechnicalFeaturesGetAsyncResponse } from '../technical-features-get-api';
import type { TechFeatureSubmitApi, TechFeatureSubmitApiRequest, TechFeatureSubmitApiResponse } from '../tech-feature-submit-api';
import type { NoveltyCheckReportTitleApi, NoveltyCheckReportTitleApiRequest, NoveltyCheckReportTitleApiResponse } from '../novelty-check-report-title-api';
import type { TechEssentialUpdateApi, TechEssentialUpdateApiRequest, TechEssentialUpdateApiResponse } from '../tech-essential-update-api';
import type { NoveltyReportSubtitleInfoApi, NoveltyReportSubtitleInfoRequest, NoveltyReportSubtitleInfoResponse } from '../novelty-report-subtitle-info-api';
import type { ComparisonPublicSituationApi, ComparisonPublicSituationRequest, ComparisonPublicSituationResponse } from '../comparison-public-situation-api';
import type { NoveltyReportConclusionApi, NoveltyReportConclusionRequest, NoveltyReportConclusionResponse } from '../comparison-public-situation-api';
import type { UpdateNoveltySimilarListApi, UpdateNoveltySimilarListRequest, UpdateNoveltySimilarListResponse } from '../update-novelty-similar-list-api';
import type { TerminateSearchAgentApi, TerminateSearchAgentApiRequest, TerminateSearchAgentApiResponse } from '../terminate-search-agent-api';
import type { NoveltyReviewApi, NoveltyReviewRequest, NoveltyReviewAsyncResponse } from '../novelty-review-api';
import type { NoveltyInputSummaryApi, SummarizeNoveltyInputRequest, NoveltyInputSummaryAsyncResponse } from '../novelty-input-summary-api';
import type { CreativityReviewApi, CreativityReviewRequest, CreativityReviewResponse } from '../creativity-review-api';
import type { NoveltyFeatureComparisonApi, NoveltyFeatureComparisonRequest, NoveltyFeatureComparisonResponse } from '@/infrastructure/api-functions/core-drafting-api/novelty-feature-comparison-api.ts';
import type { NoveltyInputLanguageDetectApi, NoveltyInputLanguageDetectRequest, NoveltyInputLanguageDetectResponse } from '../novelty-input-language-detect-api';

@injectable()
export class NoveltyApiServiceImpl extends PsHttpService
    implements
        NoveltyInputLanguageDetectApi,
        NoveltyReviewApi,
        CreativityReviewApi,
        TerminateSearchAgentApi,
        NoveltyReportConclusionApi,
        ComparisonPublicSituationApi,
        NoveltyReportSubtitleInfoApi,
        TechEssentialUpdateApi,
        NoveltyCheckReportTitleApi,
        TechFeatureSubmitApi,
        TechnicalFeaturesGetApi,
        SearchNoveltyResultApi,
        SearchNoveltyApi,
        PatentProfileByIdsApi,
        NoveltyInputSummaryApi,
        FeatureRecognizeApi,
        CreateNoveltyCheckTaskApi,
        UpdateNoveltySimilarListApi,
        NoveltyFeatureComparisonApi
{
    async detect(data: NoveltyInputLanguageDetectRequest): Promise<NoveltyInputLanguageDetectResponse> {
        const res = await this.post<PatSnapSuccessResponseData<NoveltyInputLanguageDetectResponse>>({
            url: 'ai/task/lang-detect',
            data,
        });
        return res.data;
    }

    async getNoveltyReview(params: NoveltyReviewRequest, signal?: AbortSignal): Promise<NoveltyReviewAsyncResponse> {
        const res = await this.get<PatSnapSuccessResponseData<NoveltyReviewAsyncResponse>>({
            url: `novelty-search/report/review-report/${params.taskId}`,
            signal,
        });
        return res.data;
    }

    async terminateSearchAgent(params: TerminateSearchAgentApiRequest): Promise<TerminateSearchAgentApiResponse> {
        const res = await this.post<PatSnapSuccessResponseData<TerminateSearchAgentApiResponse>>({
            url: 'novelty-search/agent/terminate',
            params,
        });
        return res.data;
    }

    async getNoveltyReportConclusion(params: NoveltyReportConclusionRequest): Promise<NoveltyReportConclusionResponse> {
        const res = await this.get<PatSnapSuccessResponseData<NoveltyReportConclusionResponse>>({
            url: `novelty-search/report/result/${params.task_id}`,
        });
        return res.data;
    }

    async getComparisonPublicSituation(params: ComparisonPublicSituationRequest): Promise<ComparisonPublicSituationResponse> {
        const res = await this.get<PatSnapSuccessResponseData<ComparisonPublicSituationResponse>>({
            url: `novelty-search/report/comparative-literature/${params.task_id}`,
        });
        return res.data;
    }

    async getNoveltyReportSubtitleInfo(params: NoveltyReportSubtitleInfoRequest): Promise<NoveltyReportSubtitleInfoResponse> {
        const res = await this.get<PatSnapSuccessResponseData<NoveltyReportSubtitleInfoResponse>>({
            url: `novelty-search/report/title/${params.task_id}`,
        });
        return res.data;
    }

    async updateTechSummary(data: TechEssentialUpdateApiRequest): Promise<TechEssentialUpdateApiResponse> {
        const res = await this.post<PatSnapSuccessResponseData<TechEssentialUpdateApiResponse>>({
            url: 'novelty-search/update-tech-essential',
            data,
        });
        return res.data;
    }

    async getNoveltyCheckReportTitle(data: NoveltyCheckReportTitleApiRequest): Promise<NoveltyCheckReportTitleApiResponse> {
        const res = await this.post<PatSnapSuccessResponseData<NoveltyCheckReportTitleApiResponse>>({
            url: 'novelty-search/title',
            data,
        });
        return res.data;
    }

    async submitTechFeature(data: TechFeatureSubmitApiRequest): Promise<TechFeatureSubmitApiResponse> {
        const res = await this.post<PatSnapSuccessResponseData<TechFeatureSubmitApiResponse>>({
            url: 'novelty-search/confirm-tech-feature',
            data,
        });
        return res.data;
    }

    async getTechnicalFeatures(data: TechnicalFeaturesGetApiRequest, signal?: AbortSignal): Promise<TechnicalFeaturesGetAsyncResponse> {
        const res = await this.get<PatSnapSuccessResponseData<TechnicalFeaturesGetAsyncResponse>>({
            url: `novelty-search/get-tech-essential-feature/${data.task_id}`,
            signal,
        });
        return res.data;
    }

    async getSearchNoveltyResult(params: SearchNoveltyResultRequest, signal?: AbortSignal): Promise<SearchNoveltyResultResponseV2> {
        const res = await this.get<PatSnapSuccessResponseData<SearchNoveltyResultResponseV2>>({
            url: 'novelty-search/agent/result',
            params,
            signal,
        });
        return res.data;
    }

    async startSearchNovelty(data: SearchNoveltyRequest): Promise<SearchNoveltyResponse> {
        const res = await this.post<PatSnapSuccessResponseData<SearchNoveltyResponse>>({
            url: 'novelty-search/agent',
            data,
        });
        return res.data;
    }

    async getPatentProfileByIds(data: PatentProfileByIdsRequest): Promise<PatentProfileByIdsResponse> {
        const res = await this.post<PatSnapSuccessResponseData<PatentProfileByIdsResponse>>({
            url: 'patent/batch-basic-info',
            data,
        });
        return res.data;
    }

    async getPatentProfileByKeyword(data: PatentProfileByKeywordRequest): Promise<PatentProfileByKeywordResponse> {
        const res = await this.post<PatSnapSuccessResponseData<PatentProfileByKeywordResponse>>({
            url: 'patent/compare-search',
            data,
            headers: {
                'X-Client-Id': import.meta.env.VITE_PASSPORT_CLIENT_ID,
            },
        });
        return res?.data;
    }

    async summarizeNoveltyInput(data: SummarizeNoveltyInputRequest, signal?: AbortSignal): Promise<NoveltyInputSummaryAsyncResponse> {
        const res = await this.post<PatSnapSuccessResponseData<NoveltyInputSummaryAsyncResponse>>({
            url: 'novelty-search/text-summary',
            data,
            signal,
        });
        return res.data;
    }

    async recognize(data: FeatureRecognizeRequest): Promise<FeatureRecognizeResponse> {
        const res = await this.post<PatSnapSuccessResponseData<FeatureRecognizeResponse>>({
            url: 'novelty-search/feature-extract',
            data,
        });
        return res.data;
    }

    async createNoveltyCheckTask(data: CreateNoveltyCheckTaskRequest): Promise<CreateNoveltyCheckTaskResponse> {
        const res = await this.post<PatSnapSuccessResponseData<CreateNoveltyCheckTaskResponse>>({
            url: 'novelty-search/submit',
            data,
        });
        return res.data;
    }

    async updateNoveltySimilarList(data: UpdateNoveltySimilarListRequest): Promise<UpdateNoveltySimilarListResponse> {
        const res = await this.post<PatSnapSuccessResponseData<UpdateNoveltySimilarListResponse>>({
            url: 'novelty-search/update-similar-list',
            data,
        });
        return res.data;
    }

    async getFeatureComparison(data: NoveltyFeatureComparisonRequest): Promise<NoveltyFeatureComparisonResponse> {
        const res = await this.post<PatSnapSuccessResponseData<NoveltyFeatureComparisonResponse>>({
            url: 'novelty-search/feature-comparison',
            data,
        });
        return res.data;
    }

    get options() {
        return {
            baseURL: coreTelecomApiUrlPrefix,
            headers: {
                'X-API-Version': '2.0',
            },
        };
    }
}
